import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  Link as RouterLink,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import {
  Entity,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Box,
  FormControlLabel,
  Link,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { StyledMTableToolbar } from '../../cards/RelatedEntitiesCard/RelatedEntitiesCard';
import { MoreInTooltip } from '../../MoreInTooltip';
import { VirtualChip, isVirtual } from '../../input/VirtualPicker';
import { VuidChip, vuidOf } from '../../VuidChip';
import {
  ServiceProviderChip,
  isServiceProvider,
} from '../../input/ServiceProvidersOnlyPicker';

/**
 * How much has to be typed before the search box narrows the tree down. One or
 * two letters match nearly every department, so the tree collapses to a wall of
 * rows that is no help to anyone; the whole tree is the better answer until the
 * third character says what is actually being looked for.
 */
const MIN_SEARCH_LENGTH = 3;

/** The typed term, or nothing at all while it is too short to search for. */
function searchedTerm(term: string): string {
  return term.trim().length < MIN_SEARCH_LENGTH ? '' : term;
}

/**
 * The search box of the table with the three character rule in front of it.
 *
 * The rule cannot live in `customFilterAndSearch` alone: material-table
 * collapses the whole tree and expands it again around whatever the search
 * leaves over as soon as the box holds a single character (`treefyData` in
 * `@material-table/core`), so a column that matches every row still makes the
 * table jump. Below the limit the table is handed an empty term instead, which
 * is the state it is in before anything is typed - the box keeps what was
 * typed, only the table is left alone.
 *
 * The gate sits on the data manager as well as on the change handler because
 * the toolbar writes the term into the data manager itself; the handler only
 * asks for the next render.
 */
function GatedToolbar(props: {
  dataManager: { changeSearchText: (term: string) => void };
  onSearchChanged: (term: string) => void;
}) {
  const { dataManager, onSearchChanged } = props;
  const gated = Object.create(dataManager);
  gated.changeSearchText = (term: string) =>
    dataManager.changeSearchText(searchedTerm(term));

  return (
    <StyledMTableToolbar
      {...props}
      dataManager={gated}
      onSearchChanged={(term: string) => onSearchChanged(searchedTerm(term))}
    />
  );
}

/** One node of the CMDB org tree, either a department or a team. */
type OrgRow = {
  id: string;
  parentId?: string;
  title: string;
  entity: Entity;
  isDepartment: boolean;
  archived: boolean;
  /** No unit of the line organisation, see `isVirtual`. */
  virtual: boolean;
  /** Title and CMDB short name, what the name column searches, lower cased. */
  searchText: string;
  /** Departments directly below, 0 for a team row. */
  departmentCount: number;
  /** Teams directly below, 0 for a team row. */
  teamCount: number;
  /** Nesting level in the rendered tree, 0 for a top level row. */
  depth: number;
};

// material-table indents a level by 9px, and only the expand toggle - a team
// row has no toggle and ends up flush with its department. The name column
// adds this much per level on top of that, kept small so that deep rows leave
// the name column room for the name.
const INDENT_PER_LEVEL = 16;

// The whole tree is rendered client side, so ask for the fields the table
// needs and nothing else - the full entities are roughly ten times the size.
const ENTITY_FIELDS = [
  'kind',
  'metadata.name',
  'metadata.namespace',
  'metadata.title',
  'metadata.tags',
  // Whether a team is a service provider, see ServiceProviderChip.
  'metadata.labels',
  'metadata.links',
  'metadata.annotations',
  'spec.type',
  'spec.parent',
  'spec.accountableContact',
  'spec.mainContacts',
  'spec.technicalContacts',
  'spec.securityContacts',
];

type ContactField =
  | 'accountableContact'
  | 'mainContacts'
  | 'technicalContacts'
  | 'securityContacts';

function contactsOf(entity: Entity, field: ContactField): any[] {
  const value = entity.spec?.[field];
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/** Title and CMDB short name of the row, for the search box. */
function searchTextOf(entity: Entity): string {
  return [entity.metadata.title, entity.metadata.name]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('en-US');
}

/**
 * The search of a contact column: the mail and user id of every contact in it.
 * material-table only searches the columns that are shown, so a row is never
 * found by a name that is nowhere on it - a hidden column gives no clue why
 * the row is there.
 */
function searchContacts(field: ContactField) {
  return (query: string, row: OrgRow) => {
    const term = query.toLocaleLowerCase('en-US');
    return contactsOf(row.entity, field).some(it =>
      [it?.mail, it?.entityRef].some(
        (value: string | undefined) =>
          value?.toLocaleLowerCase('en-US').includes(term) ?? false,
      ),
    );
  };
}

function byTitle(a: OrgRow, b: OrgRow): number {
  return a.title.localeCompare(b.title, 'en', {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * The rows by name, every department directly in front of what hangs off it.
 * material-table keeps the order it is handed within a parent, so this is the
 * order the tree starts out in - and a row that arrives before its department
 * would pull that department up to where the child stands.
 */
function inTreeOrder(rows: OrgRow[]): OrgRow[] {
  const byParent = new Map<string, OrgRow[]>();
  const roots: OrgRow[] = [];
  for (const row of rows) {
    if (row.parentId) {
      byParent.set(row.parentId, [...(byParent.get(row.parentId) ?? []), row]);
    } else {
      roots.push(row);
    }
  }

  const ordered: OrgRow[] = [];
  const emitted = new Set<string>();
  const visit = (siblings: OrgRow[]) => {
    for (const row of [...siblings].sort(byTitle)) {
      if (emitted.has(row.id)) {
        continue;
      }
      emitted.add(row.id);
      ordered.push(row);
      visit(byParent.get(row.id) ?? []);
    }
  };
  visit(roots);

  // A parent chain that loops in CMDB leaves rows without a root to descend
  // from, they go to the end rather than out of the table.
  return [...ordered, ...rows.filter(it => !emitted.has(it.id)).sort(byTitle)];
}

/** Fills in `depth` by walking `parentId` up to a row without a parent. */
function withDepth(rows: OrgRow[]): OrgRow[] {
  const parentOf = new Map(rows.map(it => [it.id, it.parentId]));
  const depths = new Map<string, number>();

  const depthOf = (id: string): number => {
    const chain: string[] = [];
    let current: string | undefined = id;
    let depth = 0;
    // A broken parent chain in CMDB must not spin here, so every id is only
    // walked once.
    while (current && !depths.has(current) && !chain.includes(current)) {
      chain.push(current);
      current = parentOf.get(current);
    }
    if (current && depths.has(current)) {
      depth = depths.get(current)! + 1;
    }
    for (const name of chain.reverse()) {
      depths.set(name, depth);
      depth += 1;
    }
    return depths.get(id)!;
  };

  return rows.map(row => ({ ...row, depth: depthOf(row.id) }));
}

export function buildOrgRows(
  entities: Entity[],
  showArchived: boolean = false,
): OrgRow[] {
  const isArchived = (it: Entity) =>
    it.metadata.tags?.includes('archived') ?? false;
  const visible = showArchived
    ? entities
    : entities.filter(it => !isArchived(it));

  // The parent chain is walked over all entities, not just the visible ones:
  // an active team below an archived department has to be re-attached to the
  // nearest department that is still shown instead of dropping to the root.
  const parentOf = new Map<string, string | undefined>(
    entities.map(it => [
      it.metadata.name,
      it.spec?.parent as string | undefined,
    ]),
  );
  const visibleNames = new Set(visible.map(it => it.metadata.name));

  const visibleParentOf = (name: string): string | undefined => {
    const seen = new Set<string>([name]);
    let parent = parentOf.get(name);
    while (parent && !seen.has(parent)) {
      if (visibleNames.has(parent)) {
        return parent;
      }
      seen.add(parent);
      parent = parentOf.get(parent);
    }
    return undefined;
  };

  const rows = visible.map(entity => ({
    id: entity.metadata.name,
    parentId: visibleParentOf(entity.metadata.name),
    title: entity.metadata.title ?? entity.metadata.name,
    entity: entity,
    isDepartment: entity.spec?.type === 'department',
    archived: isArchived(entity),
    virtual: isVirtual(entity),
    searchText: searchTextOf(entity),
    departmentCount: 0,
    teamCount: 0,
    depth: 0,
  }));

  // Only what hangs directly off a department counts towards it, anything
  // further down is counted on its own department instead - the rows that
  // opening the department brings up.
  const departmentCounts = new Map<string, number>();
  const teamCounts = new Map<string, number>();
  for (const row of rows) {
    if (!row.parentId) {
      continue;
    }
    const counts = row.isDepartment ? departmentCounts : teamCounts;
    counts.set(row.parentId, (counts.get(row.parentId) ?? 0) + 1);
  }

  return inTreeOrder(
    withDepth(
      rows.map(row => ({
        ...row,
        departmentCount: departmentCounts.get(row.id) ?? 0,
        teamCount: teamCounts.get(row.id) ?? 0,
      })),
    ),
  );
}

/**
 * Everything below `rootId`, with the direct children of the root promoted to
 * top level rows so that the sub tree renders on its own.
 */
export function subtreeOf(rows: OrgRow[], rootId: string): OrgRow[] {
  const byParent = new Map<string, OrgRow[]>();
  for (const row of rows) {
    if (!row.parentId) {
      continue;
    }
    byParent.set(row.parentId, [...(byParent.get(row.parentId) ?? []), row]);
  }
  const subtree: OrgRow[] = [];
  const queue = [...(byParent.get(rootId) ?? [])];
  while (queue.length > 0) {
    const row = queue.shift()!;
    subtree.push(
      row.parentId === rootId ? { ...row, parentId: undefined } : row,
    );
    queue.push(...(byParent.get(row.id) ?? []));
  }
  // The children of the root are top level rows now, so the indentation of the
  // whole sub tree has to move up with them.
  return inTreeOrder(withDepth(subtree));
}

/**
 * The chain of departments at the top of the tree that has only one way down.
 * Since CMDB schema v13 the org is rooted at Bertelsmann, which holds RTL
 * Group, which holds RTL Deutschland, and none of those says anything by being
 * closed - there is a single department below each of them. They start out open
 * so that the operating units are on screen, and the walk stops at the first
 * department that actually branches, which is the last one opened.
 *
 * A tree with several roots - the shape before v13 - has no such chain and
 * opens nothing.
 */
function spineOf(rows: OrgRow[]): Set<string> {
  const departmentsBelow = (id?: string) =>
    rows.filter(row => row.isDepartment && row.parentId === id);

  const spine = new Set<string>();
  let level = departmentsBelow(undefined);
  // A parent chain that loops in CMDB must not spin here, hence the `has`.
  while (level.length === 1 && !spine.has(level[0].id)) {
    spine.add(level[0].id);
    level = departmentsBelow(level[0].id);
  }
  return spine;
}

/**
 * The rows of a list by id, worked out once per list. material-table asks for
 * the parent of every row, and over and again whenever it builds the tree -
 * looked up by walking the list, that is a walk per row, and seconds for an
 * org of a thousand rows; by id it is next to nothing.
 */
const rowsByIdCache = new WeakMap<OrgRow[], Map<string, OrgRow>>();
function rowsById(all: OrgRow[]): Map<string, OrgRow> {
  let byId = rowsByIdCache.get(all);
  if (!byId) {
    byId = new Map(all.map(row => [row.id, row]));
    rowsByIdCache.set(all, byId);
  }
  return byId;
}

/**
 * What hangs directly off a department, by kind - `2 departments · 5 teams` -
 * or nothing for one that holds nothing: it has no toggle either, which says
 * as much.
 */
function contentsOf(row: OrgRow): string | undefined {
  const lower = (count: number, singular: string) =>
    counted(count, singular).toLocaleLowerCase('en-US');
  const parts = [
    ...(row.departmentCount > 0
      ? [lower(row.departmentCount, 'Department')]
      : []),
    ...(row.teamCount > 0 ? [lower(row.teamCount, 'Team')] : []),
  ];
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * A link to an entity, named and iconed the way `EntityRefLink` does it - by
 * the entity presentation of the app - but without the `Box` that one wraps
 * the name in. A `Box` gets a stylesheet of its own, and with a link in every
 * row and one per contact the tree made thousands of them whenever it was set
 * up anew, which took seconds on expand or collapse all.
 */
function TreeEntityLink(props: {
  entityRef: Entity | string;
  defaultKind: string;
}) {
  const { entityRef, defaultKind } = props;
  const theme = useTheme();
  const entityRoute = useRouteRef(entityRouteRef);
  const { primaryTitle, secondaryTitle, Icon } = useEntityPresentation(
    entityRef,
    { defaultKind },
  );
  // Looks the way `EntityDisplayName` draws it: the icon in the secondary
  // text colour, and the secondary title on a slow hover.
  const link = (
    <RouterLink
      to={entityRoute(entityRouteParams(entityRef))}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {Icon && (
        <span
          style={{
            display: 'inline-flex',
            marginRight: theme.spacing(0.5),
            color: theme.palette.text.secondary,
          }}
        >
          <Icon fontSize="inherit" />
        </span>
      )}
      {primaryTitle}
    </RouterLink>
  );
  return secondaryTitle ? (
    <Tooltip enterDelay={1500} title={secondaryTitle}>
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function createNameColumn(): TableColumn<OrgRow> {
  return {
    title: 'Name',
    field: 'title',
    defaultSort: 'asc',
    // The title and the short CMDB name (`apero`); the contacts are searched
    // by their own columns, see `searchContacts`. A term too short to search
    // for never gets here, `GatedToolbar` holds it back before the table sees
    // it.
    customFilterAndSearch: (query, row) =>
      row.searchText.includes(query.toLocaleLowerCase('en-US')),
    // Plain elements with inline styles in every cell rather than `Box`: a
    // `Box` with props gets a stylesheet of its own, and a tree of a thousand
    // rows made thousands of them each time it was set up anew - most of the
    // seconds that expanding or collapsing the whole tree took.
    render: row => (
      // Centred rather than on the baseline: a chip lined up on the baseline
      // of its label hangs below the name and makes its row taller than the
      // rest.
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          paddingLeft: row.depth * INDENT_PER_LEVEL,
        }}
      >
        {/* The icon in front of the name comes with the entity presentation,
            the app gives a department a different one than a team. */}
        <Typography
          variant="body2"
          component="span"
          style={{ fontWeight: row.isDepartment ? 700 : 400 }}
        >
          <TreeEntityLink entityRef={row.entity} defaultKind="Group" />
        </Typography>
        {/* Counted and shown like any other: a virtual team is a product
            team all the same, it only stands outside the line organisation.
            The chip says what the row is, so it stands ahead of what the row
            holds. */}
        {vuidOf(row.entity) && <VuidChip vuid={vuidOf(row.entity)!} />}
        {isServiceProvider(row.entity) && <ServiceProviderChip />}
        {row.virtual && <VirtualChip />}
        {row.isDepartment && contentsOf(row) && (
          <Typography variant="caption" color="textSecondary">
            {contentsOf(row)}
          </Typography>
        )}
        {row.archived && (
          <Typography variant="caption" color="textSecondary">
            (archived)
          </Typography>
        )}
      </div>
    ),
  };
}

/**
 * The CMDB name of a user as a ref string. A parsed ref would be handed to
 * `EntityRefLinks` as an object, and an object counts as an entity that is
 * already loaded - the link would stay on the bare user id instead of picking
 * up the display name from the catalog.
 */
function userRefOf(name: string): string {
  return stringifyEntityRef({ kind: 'User', name: name });
}

/**
 * How many contacts a cell lists before the rest goes behind `+N more`. A team
 * with five technical contacts would otherwise make its row five lines tall.
 */
const MAX_VISIBLE_CONTACTS = 2;

/**
 * One contact per line instead of `EntityRefLinks`: that one puts a comma
 * between the links, and in a narrow cell the comma wraps onto a line of its
 * own. Set a size smaller than the name column, the contacts are what takes
 * the room in a row.
 */
function ContactList(props: { refs: string[] }) {
  return (
    <Typography
      component="div"
      variant="caption"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
    >
      {props.refs.map(ref => (
        <TreeEntityLink key={ref} entityRef={ref} defaultKind="user" />
      ))}
    </Typography>
  );
}

/** `+N more` and the rest of the contacts in a tooltip, see MoreInTooltip. */
function MoreContacts(props: { refs: string[] }) {
  return (
    <MoreInTooltip count={props.refs.length}>
      <ContactList refs={props.refs} />
    </MoreInTooltip>
  );
}

function createAccountableContactColumn(): TableColumn<OrgRow> {
  return {
    title: 'Accountable Contact',
    sorting: false,
    customFilterAndSearch: searchContacts('accountableContact'),
    render: ({ entity }) => {
      const contact = entity.spec?.accountableContact as any;
      if (contact && contact.entityRef) {
        return <ContactList refs={[userRefOf(contact.entityRef)]} />;
      }
      return (
        <Typography variant="caption" style={{ color: '#888' }}>
          No Accountable Contact
        </Typography>
      );
    },
  };
}

function createContactsColumn(
  title: string,
  field: Exclude<ContactField, 'accountableContact'>,
): TableColumn<OrgRow> {
  return {
    title: title,
    sorting: false,
    customFilterAndSearch: searchContacts(field),
    render: ({ entity }) => {
      const refs = ((entity.spec?.[field] as any[]) ?? []).map(it =>
        userRefOf(it.entityRef),
      );
      if (refs.length <= MAX_VISIBLE_CONTACTS) {
        return <ContactList refs={refs} />;
      }
      return (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
        >
          <ContactList refs={refs.slice(0, MAX_VISIBLE_CONTACTS)} />
          <MoreContacts refs={refs.slice(MAX_VISIBLE_CONTACTS)} />
        </div>
      );
    },
  };
}

const TECHNICAL_CONTACTS = 'Technical Contacts';
const SECURITY_CONTACTS = 'Security Contacts';

const columns: TableColumn<OrgRow>[] = [
  // The name is what the tree is, the columns button must not hide it.
  { ...createNameColumn(), removable: false },
  createAccountableContactColumn(),
  createContactsColumn('Main Contacts', 'mainContacts'),
  createContactsColumn(TECHNICAL_CONTACTS, 'technicalContacts'),
  createContactsColumn(SECURITY_CONTACTS, 'securityContacts'),
];

/** Columns that only show once they are ticked on in the columns button. */
const HIDDEN_BY_DEFAULT = [TECHNICAL_CONTACTS, SECURITY_CONTACTS];

/**
 * The columns with the ones hidden that are hidden right now: what was ticked
 * off in the columns button, otherwise those in `HIDDEN_BY_DEFAULT`. The
 * columns are looked up by title, they have no `field` - one would make
 * material-table search them.
 */
function visibleColumns(
  chosen: Record<string, boolean>,
): TableColumn<OrgRow>[] {
  const withHidden = columns.map(column => {
    const title = column.title as string;
    const hidden = chosen[title] ?? HIDDEN_BY_DEFAULT.includes(title);
    return { ...column, hidden: hidden };
  });

  // The widths are shared out over the columns that are shown, the name
  // counting double: with the default four that is 40% and 20% each. Fixed
  // widths would not add up to 100% once a column comes or goes, and
  // material-table hands whatever is left over to the cell of the tree toggle,
  // which pushes the names off to the right - or squeezes the last column when
  // it is more than 100%.
  const weightOf = (column: TableColumn<OrgRow>) =>
    column.removable === false ? 2 : 1;
  const total = withHidden
    .filter(column => !column.hidden)
    .reduce((sum, column) => sum + weightOf(column), 0);
  return withHidden.map(column => ({
    ...column,
    width: `${(weightOf(column) / total) * 100}%`,
  }));
}

/** `1 Team`, `3 Teams` - one of the counts in the title of the table. */
function counted(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

/**
 * Which rows of the tree are open. material-table opens and closes rows by
 * itself and only reports it, and a render of the whole table for every click
 * would set the tree up anew - so this is kept outside React state, and only
 * the link that expands or collapses everything listens to it.
 */
type OpenRows = {
  /** Starts over: these rows open, of these that have rows below them. */
  reset: (open: Iterable<string>, expandable: Set<string>) => void;
  set: (id: string, isOpen: boolean) => void;
  /** Whether every row that has rows below it is open. */
  allOpen: () => boolean;
  subscribe: (listener: () => void) => () => void;
};

function createOpenRows(): OpenRows {
  let open = new Set<string>();
  let expandable = new Set<string>();
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach(listener => listener());
  return {
    reset(nextOpen, nextExpandable) {
      open = new Set(nextOpen);
      expandable = nextExpandable;
      notify();
    },
    set(id, isOpen) {
      if (isOpen) {
        open.add(id);
      } else {
        open.delete(id);
      }
      notify();
    },
    allOpen: () =>
      expandable.size > 0 && [...expandable].every(id => open.has(id)),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * One link that does what the tree is not yet: open it all as long as
 * anything is closed, close it all once everything is open - whether it got
 * there by this link or row by row.
 */
const ExpandAllLink = (props: {
  openRows: OpenRows;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) => {
  const { openRows, onExpandAll, onCollapseAll } = props;
  const allOpen = useSyncExternalStore(openRows.subscribe, openRows.allOpen);
  return (
    <Link
      component="button"
      variant="body2"
      onClick={allOpen ? onCollapseAll : onExpandAll}
    >
      {allOpen ? 'Collapse all' : 'Expand all'}
    </Link>
  );
};

/** The column the table is sorted by, by its index, and in which direction. */
type SortOrder = { column: number; direction: 'asc' | 'desc' };

/**
 * The columns with the sort the reader chose as their default, so that a table
 * set up anew sorts as before. Without a choice the columns sort as they say -
 * by name.
 */
function sortedBy(
  shown: TableColumn<OrgRow>[],
  order: SortOrder | undefined,
): TableColumn<OrgRow>[] {
  if (!order) {
    return shown;
  }
  return shown.map((column, index) => ({
    ...column,
    defaultSort: index === order.column ? order.direction : undefined,
  }));
}

export interface DepartmentTreeTableProps {
  /** Show only what is below this department instead of the whole org. */
  rootId?: string;
  title?: string;
  /** Open the departments at the top that have a single one below them. */
  expandSpine?: boolean;
  /**
   * Open this many levels from the top, so that the rows down to the level
   * below them are on screen. `5` opens the top level and the four below it.
   */
  expandLevels?: number;
  /**
   * Put the cursor into the search box as soon as the table is there, so that
   * a search can be typed straight away. Only for a page or a tab that is the
   * table and nothing else - next to other content it would take the focus off
   * everything else there.
   */
  autoFocusSearch?: boolean;
  /**
   * Offer the link that opens or closes the whole tree at once. Each click
   * sets the table up anew with every row in it: quick enough for a
   * department, too slow for the whole org.
   */
  expandAll?: boolean;
}

export const DepartmentTreeTable = (props: DepartmentTreeTableProps) => {
  const {
    rootId,
    title,
    expandSpine,
    expandLevels = 0,
    autoFocusSearch,
    expandAll,
  } = props;
  const [showArchived, setShowArchived] = useState(false);
  // Expand all and collapse all overrule how the tree starts out. material-
  // table reads `defaultExpanded` only when it is set up, so each click sets
  // it up anew - the count is its key.
  const [expansion, setExpansion] = useState<{
    mode: 'initial' | 'all' | 'none';
    count: number;
  }>({ mode: 'initial', count: 0 });
  const expandTo = (mode: 'all' | 'none') =>
    setExpansion(it => ({ mode, count: it.count + 1 }));
  const [openRows] = useState(createOpenRows);
  // What was searched for and sorted by, for when Expand all or Collapse all
  // sets the table up anew - it would forget both otherwise. Refs rather than
  // state: the table keeps both itself, these only remember them.
  const searchTerm = useRef('');
  const sortOrder = useRef<SortOrder>();
  // What was ticked on or off in the columns button, by column title. It is
  // kept here rather than left to material-table, which takes `hidden` from
  // the columns again whenever the data changes and would undo the choice.
  const [chosenColumns, setChosenColumns] = useState<Record<string, boolean>>(
    {},
  );
  const catalogApi = useApi(catalogApiRef);
  const { value, loading, error } = useAsync(async () => {
    const [groups, products] = await Promise.all([
      catalogApi.getEntities({
        filter: [
          { kind: 'Group', 'spec.type': 'department' },
          { kind: 'Group', 'spec.type': 'team' },
        ],
        fields: ENTITY_FIELDS,
      }),
      // Products are only counted in the title, so who owns one and whether it
      // is archived is all that is read of it.
      catalogApi.getEntities({
        filter: { kind: 'Product' },
        fields: ['metadata.tags', 'relations'],
      }),
    ]);
    return { groups: groups.items, products: products.items };
  }, [catalogApi]);

  const allRows = buildOrgRows(value?.groups ?? [], showArchived);
  const rows = rootId ? subtreeOf(allRows, rootId) : allRows;
  const spine = expandSpine ? spineOf(rows) : undefined;
  // How the tree starts out: every level closed - all but the spine, which
  // hides the rows worth reading behind itself without saying anything of its
  // own, and the levels from the top that were asked to be open - unless
  // Expand all or Collapse all said otherwise.
  const startsOpen = (row: OrgRow) => {
    if (expansion.mode !== 'initial') {
      return expansion.mode === 'all';
    }
    return Boolean(spine?.has(row.id)) || row.depth < expandLevels;
  };

  // The open rows start over whenever the table is set up anew.
  useEffect(() => {
    const ids = new Set(rows.map(row => row.id));
    const expandable = new Set(
      rows.flatMap(row =>
        row.parentId && ids.has(row.parentId) ? [row.parentId] : [],
      ),
    );
    openRows.reset(
      rows
        .filter(row => expandable.has(row.id) && startsOpen(row))
        .map(row => row.id),
      expandable,
    );
    // `rows` is a fresh array on every render; what it is made of is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openRows,
    value,
    showArchived,
    rootId,
    expandSpine,
    expandLevels,
    expansion,
  ]);

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  const departmentCount = rows.filter(it => it.isDepartment).length;
  const teamCount = rows.length - departmentCount;
  // A product is counted where the team that owns it stands, so a department
  // page only counts what its own teams own.
  const teamRefs = new Set(
    rows
      .filter(it => !it.isDepartment)
      .map(it => stringifyEntityRef(it.entity)),
  );
  const productCount = (value?.products ?? []).filter(
    product =>
      // `metadata` is missing on a product without tags: the catalog returns
      // only the fields asked for that exist, and no other field of it is.
      (showArchived || !product.metadata?.tags?.includes('archived')) &&
      product.relations?.some(
        rel => rel.type === RELATION_OWNED_BY && teamRefs.has(rel.targetRef),
      ),
  ).length;

  return (
    <>
      <Box mb={2}>
        <Alert severity="info">
          The organisational structure shown below is not an authoritative
          source. It is built from the{' '}
          <Link
            href="https://gitlab.com/dataalliance/infra/common/cmdb"
            target="_blank"
            rel="noopener noreferrer"
          >
            Developer Platform database
          </Link>{' '}
          that is partially linked with{' '}
          <Link
            href="https://serviceportal.netrtl.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            ServiceNow
          </Link>{' '}
          and can lag behind or diverge from the actual organization.
        </Alert>
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        {expandAll && (
          <Box mr={3}>
            <ExpandAllLink
              openRows={openRows}
              onExpandAll={() => expandTo('all')}
              onCollapseAll={() => expandTo('none')}
            />
          </Box>
        )}
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={showArchived}
              onChange={event => setShowArchived(event.target.checked)}
            />
          }
          label="Show archived"
        />
      </Box>
      <Table<OrgRow>
        key={expansion.count}
        title={`${title ?? 'Organization'} (${counted(
          departmentCount,
          'Department',
        )}, ${counted(teamCount, 'Team')}, ${counted(
          productCount,
          'Product',
        )})`}
        columns={sortedBy(visibleColumns(chosenColumns), sortOrder.current)}
        onChangeColumnHidden={(column, hidden) =>
          setChosenColumns(it => ({ ...it, [column.title as string]: hidden }))
        }
        onSearchChange={(term: string) => {
          searchTerm.current = term;
        }}
        onOrderChange={(column: number, direction: 'asc' | 'desc') => {
          sortOrder.current =
            column < 0 ? undefined : { column: column, direction: direction };
        }}
        onTreeExpandChange={(row: OrgRow, isExpanded: boolean) =>
          openRows.set(row.id, isExpanded)
        }
        data={rows}
        components={{ Toolbar: GatedToolbar }}
        // The parent has to be looked up in `all`: material-table copies every
        // row to attach its own `tableData` and only accepts those copies back,
        // returning anything else makes it read `tableData` of undefined.
        parentChildData={(row, all) =>
          row.parentId ? rowsById(all).get(row.parentId) : undefined
        }
        options={{
          // The tree is only a few hundred rows, paging it would cut branches
          // in half.
          paging: false,
          // A whole org or department at once is more than anyone reads, see
          // `startsOpen`.
          defaultExpanded: startsOpen,
          // What was searched for before the table was set up anew.
          searchText: searchTerm.current,
          // A third click would clear the sorting and drop the rows back into
          // catalog order while the arrow still points up, which looks broken.
          thirdSortClick: false,
          padding: 'dense',
          // The header has to stay in sight while a long tree is scrolled.
          // `position: sticky` only sticks to the nearest scrolling box, and
          // the page is not it - material-table wraps the table into a box of
          // its own for the horizontal scroll bar. So the table gets a height
          // and scrolls inside it, which makes that box the one to stick to.
          maxBodyHeight: 'calc(100vh - 160px)',
          headerStyle: { position: 'sticky', top: 0, zIndex: 10 },
          columnsButton: true,
          // Handed on to the search box as `autoFocus`; `GatedToolbar` passes
          // it through with the rest. The table is only mounted once the data
          // is in, so the focus lands when there is something to search.
          searchAutoFocus: autoFocusSearch,
        }}
      />
    </>
  );
};
